import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X, Heart, ShoppingCart } from 'lucide-react';

const ToastContext = createContext();

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  cart: ShoppingCart,
  wishlist: Heart,
};

const COLORS = {
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/80',
    border: 'border-emerald-200 dark:border-emerald-800',
    icon: 'text-emerald-500',
    text: 'text-emerald-900 dark:text-emerald-100',
    progress: 'bg-emerald-500',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-950/80',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-500',
    text: 'text-red-900 dark:text-red-100',
    progress: 'bg-red-500',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/80',
    border: 'border-amber-200 dark:border-amber-800',
    icon: 'text-amber-500',
    text: 'text-amber-900 dark:text-amber-100',
    progress: 'bg-amber-500',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/80',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-500',
    text: 'text-blue-900 dark:text-blue-100',
    progress: 'bg-blue-500',
  },
  cart: {
    bg: 'bg-violet-50 dark:bg-violet-950/80',
    border: 'border-violet-200 dark:border-violet-800',
    icon: 'text-violet-500',
    text: 'text-violet-900 dark:text-violet-100',
    progress: 'bg-violet-500',
  },
  wishlist: {
    bg: 'bg-pink-50 dark:bg-pink-950/80',
    border: 'border-pink-200 dark:border-pink-800',
    icon: 'text-pink-500',
    text: 'text-pink-900 dark:text-pink-100',
    progress: 'bg-pink-500',
  },
};

let globalToastFn = null;

// Global function — call from anywhere without hooks
export const toast = {
  success: (title, opts) => globalToastFn?.({ type: 'success', title, ...opts }),
  error: (title, opts) => globalToastFn?.({ type: 'error', title, ...opts }),
  warning: (title, opts) => globalToastFn?.({ type: 'warning', title, ...opts }),
  info: (title, opts) => globalToastFn?.({ type: 'info', title, ...opts }),
  cart: (title, opts) => globalToastFn?.({ type: 'cart', title, ...opts }),
  wishlist: (title, opts) => globalToastFn?.({ type: 'wishlist', title, ...opts }),
};

function SingleToast({ id, type = 'success', title, description, duration = 2500, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const colors = COLORS[type] || COLORS.info;
  const Icon = ICONS[type] || ICONS.info;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
      if (pct > 0) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(id), 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, id, onDismiss]);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => onDismiss(id), 300);
  };

  return (
    <div
      className={`
        w-full max-w-[340px] mx-auto overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-xl
        transition-all duration-300 ease-out
        ${colors.bg} ${colors.border}
        ${visible && !exiting ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'}
      `}
      style={{ pointerEvents: 'auto' }}
      role="alert"
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
          <Icon size={20} fill={type === 'wishlist' ? 'currentColor' : 'none'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[13px] font-semibold leading-snug ${colors.text}`}>
            {title}
          </p>
          {description && (
            <p className={`text-[11px] mt-0.5 opacity-75 ${colors.text}`}>{description}</p>
          )}
        </div>
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 rounded-lg opacity-40 hover:opacity-100 transition-opacity"
        >
          <X size={14} />
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-[2px] w-full bg-black/5 dark:bg-white/5">
        <div
          className={`h-full ${colors.progress} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((opts) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-3), { id, ...opts }]); // max 4 toasts
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Register the global function
  useEffect(() => {
    globalToastFn = addToast;
    return () => { globalToastFn = null; };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {/* Toast container — fixed at bottom on mobile, top-right on desktop */}
      <div
        className="fixed z-[99999] flex flex-col gap-2.5 pointer-events-none
          bottom-4 left-4 right-4
          sm:bottom-auto sm:top-4 sm:left-auto sm:right-4 sm:w-[360px]"
      >
        {toasts.map(t => (
          <SingleToast key={t.id} {...t} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export default toast;
